import React, { useId } from 'react';

/**
 * A HUD panel: 1px line border, corner brackets, optional mono title bar.
 *
 *   <HudPanel title="Hot leads" meta="12" actions={<button…/>}>…</HudPanel>
 *
 * Props
 *   title         string | node — mono uppercase title bar (omit for no bar)
 *   meta          node — dim mono text beside the title (counts, filters)
 *   actions       node — right-aligned controls in the title bar
 *   tone          'accent' (default) | 'gold' | 'danger' — bracket + marker colour
 *   brackets      bool, default true — corner brackets
 *   flush         bool — body without padding (lists, tables)
 *   as            element type, default 'section'
 *   headingLevel  2–6, default 2 — level of the title element
 *   bodyClassName extra classes for the body
 *   …rest         passed to the root element
 *
 * When a title is given, the root is labelled by it (aria-labelledby).
 */
export default function HudPanel({
  title, meta, actions, tone = 'accent', brackets = true, flush = false,
  as: Tag = 'section', headingLevel = 2, className = '', bodyClassName = '',
  children, ...rest
}) {
  const id = useId();
  const titleId = `hud-panel-${id}`;
  const Heading = `h${Math.min(Math.max(headingLevel, 2), 6)}`;
  const classes = [
    'hud-panel',
    brackets && 'has-brackets',
    tone && tone !== 'accent' && `tone-${tone}`,
    className,
  ].filter(Boolean).join(' ');
  const hasHead = title || meta || actions;

  return (
    <Tag className={classes} aria-labelledby={title ? titleId : undefined} {...rest}>
      {hasHead && (
        <div className="hud-panel-head">
          {title && <Heading id={titleId} className="hud-panel-title">{title}</Heading>}
          {meta != null && meta !== false && <span className="hud-panel-meta">{meta}</span>}
          {actions && <div className="hud-panel-actions">{actions}</div>}
        </div>
      )}
      <div className={['hud-panel-body', flush && 'flush', bodyClassName].filter(Boolean).join(' ')}>
        {children}
      </div>
    </Tag>
  );
}
